import type { Demo } from "@/types";
import { ChartData, ChartOptions } from "chart.js";
import { Button } from "primereact/button";
import { Chart } from "primereact/chart";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputNumber } from "primereact/inputnumber";
import { Tag } from "primereact/tag";
import { useContext, useEffect, useRef, useState } from "react";
import { LayoutContext } from "../layout/context/layoutcontext";
import Layout from "../layout/Layout";

// In your types file
export interface WaterSource {
    name: string;
    capacity: number; // in million cubic meters
    currentLevel: number; // in million cubic meters
    status: "optimal" | "stable" | "warning" | "critical";
    date: string;
}

export interface WaterSystem {
    city: string;
    consumption: number; // in liters
    status: string;
    lastUpdated: string;
}

const AdminDashboard = () => {
    const [chartOptions, setChartOptions] = useState({});
    const [chartData, setChartData] = useState({});
    const [reservoirLevel, setReservoirLevel] = useState(0);
    const { layoutConfig } = useContext(LayoutContext);
    const dt = useRef<DataTable<WaterSystem[]>>(null);

    // Bulawayo-specific data
    const waterSources: WaterSource[] = [
        { name: "Inyankuni Dam", capacity: 45.8, currentLevel: 32.1, status: "stable", date: "06/04/2023" },
        { name: "Insiza Dam", capacity: 35.2, currentLevel: 28.5, status: "warning", date: "07/04/2023" },
        { name: "Mtshabezi Dam", capacity: 28.7, currentLevel: 22.3, status: "critical", date: "12/04/2023" },
        { name: "Upper Ncema Dam", capacity: 42.5, currentLevel: 38.7, status: "optimal", date: "17/04/2023" },
        { name: "Lower Ncema Dam", capacity: 39.8, currentLevel: 31.2, status: "stable", date: "20/04/2023" },
    ];

    const formatWaterVolume = (value: number) => {
        return `${value.toFixed(1)}M m³`;
    };

    const formatPercentage = (current: number, capacity: number) => {
        return `${((current / capacity) * 100).toFixed(1)}%`;
    };

    const initChart = () => {
        const documentStyle = getComputedStyle(document.documentElement);
        const textColor = documentStyle.getPropertyValue("--text-color");
        const textColorSecondary = documentStyle.getPropertyValue("--text-color-secondary");
        const surfaceBorder = documentStyle.getPropertyValue("--surface-border");

        const data: ChartData = {
            labels: [
                "January", "February", "March", "April", "May", "June", "July",
                "August", "September", "October", "November", "December"
            ],
            datasets: [
                {
                    label: "Water Consumption (ML/day)",
                    data: [110, 115, 108, 102, 98, 95, 92, 90, 94, 100, 105, 108],
                    fill: false,
                    tension: 0.4,
                    borderColor: documentStyle.getPropertyValue("--blue-500"),
                },
                {
                    label: "Reservoir Levels (%)",
                    data: [65, 62, 58, 55, 52, 48, 45, 42, 40, 38, 35, 32],
                    fill: true,
                    borderColor: "#4CAF50",
                    tension: 0.4,
                    backgroundColor: "rgba(76, 175, 80, 0.2)",
                },
                {
                    label: "Water Loss (%)",
                    data: [28, 27, 26, 25, 24, 23, 22, 21, 20, 19, 18, 17],
                    fill: false,
                    borderColor: "#FF6B6B",
                    tension: 0.4,
                    borderDash: [5, 5],
                },
            ],
        };

        const options: ChartOptions = {
            animation: {
                duration: 0,
            },
            plugins: {
                legend: {
                    labels: {
                        color: textColor,
                    },
                },
                tooltip: {
                    callbacks: {
                        label: function (context) {
                            let label = context.dataset.label || "";
                            
                            if (label) {
                                label += ": ";
                            }
                            
                            if (context.dataset.label?.includes("Water Consumption")) {
                                label += `${context.parsed.y} ML/day`;
                            } else if (context.dataset.label?.includes("Reservoir Levels")) {
                                label += `${context.parsed.y}%`;
                            } else if (context.dataset.label?.includes("Water Loss")) {
                                label += `${context.parsed.y}%`;
                            }
                            return label;
                        },
                    },
                },
            },
            scales: {
                x: {
                    ticks: {
                        color: textColorSecondary,
                    },
                    grid: {
                        color: surfaceBorder,
                    },
                },
                y: {
                    ticks: {
                        color: textColorSecondary,
                    },
                    grid: {
                        color: surfaceBorder,
                    },
                },
            },
        };

        setChartData(data);
        setChartOptions(options);
    };

    useEffect(() => {
        initChart();
    }, [layoutConfig]);

    const nameBodyTemplate = (rowData: WaterSource) => {
        return (
            <>
                <span className="p-column-title">Water Source</span>
                <div className="flex flex-column">
                    <span className="font-medium">{rowData.name}</span>
                    <span className="text-sm text-color-secondary">Capacity: {formatWaterVolume(rowData.capacity)}</span>
                </div>
            </>
        );
    };

    const levelBodyTemplate = (rowData: WaterSource) => {
        const percentage = (rowData.currentLevel / rowData.capacity) * 100;
        return (
            <>
                <span className="p-column-title">Current Level</span>
                <div className="flex flex-column">
                    <span className="font-medium">{formatWaterVolume(rowData.currentLevel)}</span>
                    <span className="text-sm">{formatPercentage(rowData.currentLevel, rowData.capacity)}</span>
                    <div className="mt-1 w-full bg-gray-200 rounded-full h-2">
                        <div 
                            className="h-2 rounded-full"
                            style={{
                                width: `${percentage}%`,
                                backgroundColor: percentage > 60 ? '#4CAF50' : percentage > 40 ? '#FFA726' : '#F44336'
                            }}
                        ></div>
                    </div>
                </div>
            </>
        );
    };

    const dateBodyTemplate = (rowData: WaterSource) => {
        return (
            <>
                <span className="p-column-title">Last Updated</span>
                {rowData.date}
            </>
        );
    };

    const statusBodyTemplate = (rowData: WaterSource) => {
        return (
            <>
                {rowData.status === "optimal" ? (
                    <Tag value="OPTIMAL" severity="success"></Tag>
                ) : rowData.status === "stable" ? (
                    <Tag value="STABLE" severity="info"></Tag>
                ) : rowData.status === "warning" ? (
                    <Tag value="WARNING" severity="warning"></Tag>
                ) : (
                    <Tag value="CRITICAL" severity="danger"></Tag>
                )}
            </>
        );
    };

    return (
      <Layout>
        <div className="grid">
            {/* Header Section */}
            <div className="col-12">
                <div className="flex flex-column sm:flex-row align-items-center gap-4">
                    <div className="flex flex-column sm:flex-row align-items-center gap-3">
                      
                        <div className="flex flex-column align-items-center sm:align-items-start">
                            <span className="text-900 font-bold text-4xl">
                                Bulawayo Water Management Dashboard
                            </span>
                            <p className="text-600 m-0">
                                Intelligent Water Management System for Bulawayo, Zimbabwe
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-2 sm:ml-auto">
                        <Button
                            type="button"
                            tooltip="Generate Report"
                            tooltipOptions={{ position: "bottom" }}
                            icon="pi pi-file-pdf"
                            outlined
                            rounded
                        ></Button>
                        <Button
                            type="button"
                            tooltip="Send Alert"
                            tooltipOptions={{ position: "bottom" }}
                            icon="pi pi-bell"
                            outlined
                            rounded
                        ></Button>
                        <Button
                            type="button"
                            tooltip="Emergency Protocol"
                            tooltipOptions={{ position: "bottom" }}
                            icon="pi pi-shield"
                            rounded
                        ></Button>
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="col-12 md:col-6 xl:col-3">
                <div className="card h-full relative overflow-hidden">
                    <div className="flex flex-column">
                        <div className="text-xl font-semibold mb-3 text-primary">
                            Total Reservoir Capacity
                        </div>
                        <div className="mb-1 font-semibold">Currently Available</div>
                        <div className="text-2xl mb-5 font-bold">152.8M m³</div>
                        <div className="flex align-items-center justify-content-between">
                            <span className="text-lg">45.2% Full</span>
                            <i className="pi pi-water text-primary text-2xl"></i>
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="col-12 md:col-6 xl:col-3">
                <div className="card h-full">
                    <div className="flex align-items-center justify-content-between mb-3">
                        <div className="text-900 text-xl font-semibold">
                            Daily Consumption
                        </div>
                        <i className="pi pi-chart-line text-primary text-2xl"></i>
                    </div>
                    <div className="text-600 mb-1 font-semibold">Average</div>
                    <div className="text-900 text-2xl text-primary mb-5 font-bold">
                        98.5 ML/day
                    </div>
                    <div className="flex align-items-center justify-content-between">
                        <span className="text-900 text-sm">↓ 12% from last month</span>
                        <Tag value="IMPROVING" severity="success" className="text-xs"></Tag>
                    </div>
                </div>
            </div>
            
            <div className="col-12 md:col-6 xl:col-3">
                <div className="card h-full flex flex-column align-items-center justify-content-center">
                    <i className="pi pi-bolt text-primary text-4xl mb-4"></i>
                    <span className="text-900 text-lg mb-4 font-medium">
                        Water Loss
                    </span>
                    <span className="text-900 text-2xl text-primary font-bold">
                        19.7%
                    </span>
                    <span className="text-sm text-color-secondary mt-2">AI Reduction Target: 35%</span>
                </div>
            </div>
            
            <div className="col-12 md:col-6 xl:col-3">
                <div className="card h-full flex flex-column align-items-center justify-content-center">
                    <i className="pi pi-users text-primary text-4xl mb-4"></i>
                    <span className="text-900 text-lg mb-4 font-medium">
                        Population Served
                    </span>
                    <span className="text-900 text-2xl text-primary font-bold">
                        1.2M
                    </span>
                    <span className="text-sm text-color-secondary mt-2">Residents</span>
                </div>
            </div>

            {/* Recent Alerts */}
            <div className="col-12 xl:col-4">
                <div className="card">
                    <div className="text-900 text-xl font-semibold mb-3">
                        Recent System Alerts
                    </div>
                    <ul className="list-none p-0 m-0">
                        <li className="flex align-items-center p-3 mb-3 border-bottom-1 surface-border">
                            <div className="bg-red-100 p-2 border-round mr-3">
                                <i className="pi pi-exclamation-triangle text-red-500"></i>
                            </div>
                            <div className="flex flex-column">
                                <span className="text-lg font-medium text-900 mb-1">
                                    Mtshabezi Dam - Critical Level
                                </span>
                                <span>Alert triggered: 04/12/2023</span>
                            </div>
                            <Tag value="HIGH" severity="danger" className="ml-auto"></Tag>
                        </li>
                        <li className="flex align-items-center p-3 mb-3 border-bottom-1 surface-border">
                            <div className="bg-yellow-100 p-2 border-round mr-3">
                                <i className="pi pi-exclamation-circle text-yellow-500"></i>
                            </div>
                            <div className="flex flex-column">
                                <span className="text-lg font-medium text-900 mb-1">
                                    Pressure Drop - Suburbs
                                </span>
                                <span>Magwegwe Reservoir: 04/11/2023</span>
                            </div>
                            <Tag value="MEDIUM" severity="warning" className="ml-auto"></Tag>
                        </li>
                        <li className="flex align-items-center p-3 mb-3 border-bottom-1 surface-border">
                            <div className="bg-blue-100 p-2 border-round mr-3">
                                <i className="pi pi-info-circle text-blue-500"></i>
                            </div>
                            <div className="flex flex-column">
                                <span className="text-lg font-medium text-900 mb-1">
                                    Maintenance Scheduled
                                </span>
                                <span>Nzamo Water Treatment: 04/20/2023</span>
                            </div>
                            <Tag value="INFO" severity="info" className="ml-auto"></Tag>
                        </li>
                        <li className="flex align-items-center p-3 mb-3 border-bottom-1 surface-border">
                            <div className="bg-green-100 p-2 border-round mr-3">
                                <i className="pi pi-check-circle text-green-500"></i>
                            </div>
                            <div className="flex flex-column">
                                <span className="text-lg font-medium text-900 mb-1">
                                    Leak Detected & Fixed
                                </span>
                                <span>Emakhandeni Area: 04/15/2023</span>
                            </div>
                            <Tag value="RESOLVED" severity="success" className="ml-auto"></Tag>
                        </li>
                        <li className="flex align-items-center p-3">
                            <div className="bg-blue-100 p-2 border-round mr-3">
                                <i className="pi pi-cloud-download text-blue-500"></i>
                            </div>
                            <div className="flex flex-column">
                                <span className="text-lg font-medium text-900 mb-1">
                                    Rainfall Forecast
                                </span>
                                <span>Expected: 25mm in next 48h</span>
                            </div>
                            <Tag value="FORECAST" severity="info" className="ml-auto"></Tag>
                        </li>
                    </ul>
                </div>
            </div>

            {/* Main Chart */}
            <div className="col-12 xl:col-8">
                <div className="card">
                    <div className="flex align-items-center justify-content-between mb-3">
                        <div className="text-900 text-xl font-semibold">
                            Annual Water Metrics - Bulawayo
                        </div>
                        <div className="flex gap-2">
                            <Button label="2023" size="small" outlined />
                            <Button label="2024" size="small" />
                        </div>
                    </div>
                    <Chart
                        type="line"
                        data={chartData}
                        options={chartOptions}
                    ></Chart>
                </div>
            </div>

            {/* Reservoir Management */}
            <div className="col-12 lg:col-6">
                <div className="card h-full">
                    <div className="flex align-items-center justify-content-between mb-3">
                        <div className="text-900 text-xl font-semibold">
                            Reservoir Management
                        </div>
                        <Button
                            type="button"
                            icon="pi pi-plus"
                            label="Add Source"
                            outlined
                            size="small"
                        ></Button>
                    </div>
                    <div className="flex flex-column row-gap-3">
                        <div className="flex flex-column lg:flex-row gap-3">
                            <div className="w-full lg:w-6 p-3 border-1 border-round surface-border flex align-items-center hover:surface-100 cursor-pointer border-radius">
                                <div className="bg-blue-100 p-2 border-round mr-2">
                                    <i className="pi pi-water text-blue-500"></i>
                                </div>
                                <div className="flex flex-column">
                                    <span className="text-900 font-medium">
                                        Inyankuni Dam
                                    </span>
                                    <span className="text-sm text-color-secondary">32.1M m³ (70.1%)</span>
                                </div>
                            </div>
                            <div className="w-full lg:w-6 p-3 border-1 border-round surface-border flex align-items-center hover:surface-100 cursor-pointer border-radius">
                                <div className="bg-yellow-100 p-2 border-round mr-2">
                                    <i className="pi pi-exclamation-triangle text-yellow-500"></i>
                                </div>
                                <div className="flex flex-column">
                                    <span className="text-900 font-medium">
                                        Mtshabezi Dam
                                    </span>
                                    <span className="text-sm text-color-secondary">22.3M m³ (77.7%)</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-column lg:flex-row gap-3">
                            <div className="w-full lg:w-6 p-3 border-1 border-round surface-border flex align-items-center hover:surface-100 cursor-pointer border-radius">
                                <div className="bg-green-100 p-2 border-round mr-2">
                                    <i className="pi pi-check-circle text-green-500"></i>
                                </div>
                                <div className="flex flex-column">
                                    <span className="text-900 font-medium">
                                        Upper Ncema Dam
                                    </span>
                                    <span className="text-sm text-color-secondary">38.7M m³ (91.1%)</span>
                                </div>
                            </div>
                            <div className="w-full lg:w-6 p-3 border-1 border-round surface-border flex align-items-center hover:surface-100 cursor-pointer border-radius">
                                <div className="bg-blue-100 p-2 border-round mr-2">
                                    <i className="pi pi-water text-blue-500"></i>
                                </div>
                                <div className="flex flex-column">
                                    <span className="text-900 font-medium">
                                        Lower Ncema Dam
                                    </span>
                                    <span className="text-sm text-color-secondary">31.2M m³ (78.4%)</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-column sm:flex-row gap-3 mt-5">
                        <div className="flex-1 p-fluid">
                            <span className="p-float-label">
                                <InputNumber
                                    id="reservoirLevel"
                                    value={reservoirLevel}
                                    onValueChange={(e) => setReservoirLevel(e.value ?? 0)}
                                    mode="decimal"
                                    min={0}
                                    max={100}
                                    suffix="%"
                                />
                                <label htmlFor="reservoirLevel">Set Target Level</label>
                            </span>
                        </div>
                        <Button type="button" label="Update Targets" icon="pi pi-save"></Button>
                    </div>
                </div>
            </div>

           
        </div>
      </Layout>
    );
};

export default AdminDashboard;